import styles from './CartRoute.module.scss';

import { RouteFC } from 'util/routeTypes';
import { memo, useState, useRef, useCallback } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { useBodyWithoutScroll } from 'hooks/useBodyWithoutScroll/useBodyWithoutScroll';
import { CloseButton } from 'components/CloseButton';
import { Router } from '@reach/router';
import { EditGiftCardModal } from 'routes/GiftCard/Modal';
import { EditMenuItemModal } from 'routes/MenuItemModal/EditMenuItemModal';
import { CartPage } from './CartPage';
import { CSSTransition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { MerchantTitle } from 'components/MerchantTitle';
import { useHandleFocus } from 'hooks/useHandleFocus';
import { DeliveryModalError } from 'routes/DeliveryAddressModal/components/DeliveryModalError';

export const CartRoute: RouteFC<{ children: never }> = memo(() => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [showHeader, setShowHeader] = useState(true);
  const toggleShowHeader = useCallback(() => setShowHeader((current: any) => !current), []);
  const onClose = useCloseNavigate('cart');
  const nodeRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useBodyWithoutScroll();

  // Bring focus into the cart for a11y
  useHandleFocus(containerRef, isOpen);

  return (
    <>
      <MerchantTitle i18nKey="helmet.titleCart" />
      <CSSTransition
      in={ isOpen }
      timeout={{ exit: 500 }}
      nodeRef={nodeRef}
      >
        {(state: TransitionStatus) => {
          const isOpening = ['entering', 'entered'].includes(state);
          const isClosing = ['exiting', 'exited', 'unmounted'].includes(state);

          return (
            <div className={styles.overlay} ref={nodeRef}>
              <div className={cx(styles.modalContainer, {
                  [styles.entering]: isOpening,
                  [styles.exiting]: isClosing
                })}>
                <div className={cx(styles.headerContainer, {
                  [styles.displayNone]: !showHeader,
                  [styles.displayFlex]: showHeader
                })}>
                  <h2 className={styles.header} ref={containerRef} tabIndex={-1}>{t('cart.title')}</h2>
                  <CloseButton onClick={() => {setIsOpen(false); onClose();}} />
                </div>
                <CartPage closeCartPreview={onClose} toggleShowHeader={toggleShowHeader} />
              </div>
            </div>
          );
        }}
      </CSSTransition>
      <Router primary={false}>
        <EditGiftCardModal path="gift-card/:index" />
        <EditMenuItemModal path="item/:cartId" />
        <DeliveryModalError path="address-error" />
      </Router>
    </>
  );
});
